import { useId, type ComponentPropsWithoutRef } from 'react';

import type {
  AriaLabelingProps,
  DOMProps,
} from '@dynatrace/strato-components/core';

/** @internal */
export interface AriaDialogProps extends DOMProps, AriaLabelingProps {
  /**
   * The accessibility role for the dialog.
   * @defaultValue 'dialog'
   */
  role?: 'dialog' | 'alertdialog';
}

/** @internal */
export interface DialogAria {
  /** Props for the dialog container element. */
  dialogProps: ComponentPropsWithoutRef<'dialog'>;

  /** Props for the dialog title element. */
  titleProps: ComponentPropsWithoutRef<'title'>;
}

/**
 * Provides the behavior and accessibility implementation for a dialog component.
 * A dialog is an overlay shown above other content in an application.
 * @internal
 */
export function useDialog(props: AriaDialogProps): DialogAria {
  const {
    role = 'dialog',
    'aria-labelledby': ariaLabelledBy,
    ...remainingProps
  } = props;
  const uniqueId = useId();

  const titleId: string | undefined = props['aria-label']
    ? undefined
    : uniqueId;

  return {
    dialogProps: {
      role,
      tabIndex: -1,
      'aria-labelledby': ariaLabelledBy || titleId,
      ...remainingProps,
    },
    titleProps: {
      id: titleId,
    },
  };
}
